/**
 * Fragt die Uhrzeit vom Server ab, damit diese zur Anzeige genutzt werden kann.
 */
let serverTime = null;

export async function getServerTime(callback) {
    const response = await fetch(window.location.href, { method: 'HEAD' });

    if (serverTime)
    {
        if (callback) callback(serverTime);
        return;
    }

    const serverDateHeader = response.headers.get('Date');
    if (!serverDateHeader) {
        // console.error("serverTime: Fehler beim Abrufen der Serverzeit:", "Kein 'Date'-Header in der Serverantwort gefunden");
        serverTime = new Date();
    }
    else
    {
        serverTime = new Date(serverDateHeader);
    }

    if (callback) callback(serverTime);
}

function checkServerTime() {
    if (!serverTime) {
        throw new Error("Serverzeit ist nicht gesetzt. Bitte rufen Sie zuerst getServerTime() auf.");
    }
}

export function getDate()
{
    checkServerTime();

    return serverTime;
}

/**
 * Validiert Datum- und Zeitparameter
 * @param {number} year - Jahr (4-stellig)
 * @param {number} month - Monat (1-12)
 * @param {number} day - Tag (1-31)
 * @param {number} [hours=0] - Stunden (0-23)
 * @param {number} [minutes=0] - Minuten (0-59)
 * @param {number} [seconds=0] - Sekunden (0-59)
 * @throws {Error} - Wenn ungültige Werte übergeben werden
 */
function validateDateTime(year, month, day, hours = 0, minutes = 0, seconds = 0) {
    // Prüfe ob alle Parameter Zahlen sind
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) ||
        !Number.isInteger(hours) || !Number.isInteger(minutes) || !Number.isInteger(seconds)) {
        throw new Error('Alle Parameter müssen ganze Zahlen sein');
    }

    // Prüfe Jahr (4-stellig)
    if (year < 1000 || year > 9999) {
        throw new Error('Jahr muss 4-stellig sein (1000-9999): ' + year);
    }

    // Prüfe Monat (1-12)
    if (month < 1 || month > 12) {
        throw new Error('Monat muss zwischen 1 und 12 liegen: ' + month);
    }

    // Prüfe Tag (1-31, abhängig vom Monat)
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) {
        throw new Error(`Tag muss zwischen 1 und ${daysInMonth} für den Monat ${month} liegen: ` + day);
    }

    // Prüfe Stunden (0-23)
    if (hours < 0 || hours > 23) {
        throw new Error('Stunden müssen zwischen 0 und 23 liegen');
    }

    // Prüfe Minuten (0-59)
    if (minutes < 0 || minutes > 59) {
        throw new Error('Minuten müssen zwischen 0 und 59 liegen: ' + minutes);
    }

    // Prüfe Sekunden (0-59)
    if (seconds < 0 || seconds > 59) {
        throw new Error('Sekunden müssen zwischen 0 und 59 liegen: ' + seconds);
    }
}

/**
 * Prüft ob das angegebene Datum dem heutigen Datum entspricht
 * @param {number} year - Jahr (4-stellig, z.B. 2024)
 * @param {number} month - Monat (1-12)
 * @param {number} day - Tag des Monats (1-31)
 * @returns {boolean} - True wenn das angegebene Datum heute ist
 * @throws {Error} - Wenn die Serverzeit nicht initialisiert wurde oder ungültige Datumswerte übergeben werden
 */
export function isToday(year, month, day) {
    checkServerTime();
    validateDateTime(year, month, day);

    return (
        serverTime.getFullYear() === year &&
        serverTime.getMonth() === month - 1 &&
        serverTime.getDate() === day
    );
}

/**
 * Prüft ob die aktuelle Serverzeit nach dem angegebenen Zeitpunkt liegt
 * @param {number} year - Jahr (4-stellig, z.B. 2024)
 * @param {number} month - Monat (1-12)
 * @param {number} day - Tag des Monats (1-31)
 * @param {number} [hours=0] - Stunden (0-23)
 * @param {number} [minutes=0] - Minuten (0-59)
 * @param {number} [seconds=0] - Sekunden (0-59)
 * @returns {boolean} - True, wenn die Serverzeit nach dem angegebenen Zeitpunkt liegt
 * @throws {Error} - Wenn die Serverzeit nicht initialisiert wurde oder ungültige Datums-/Zeitwerte übergeben werden
 */
export function isAfter(year, month, day, hours = 0, minutes = 0, seconds = 0) {
    checkServerTime();
    validateDateTime(year, month, day, hours, minutes, seconds);

    const targetDate = new Date(year, month - 1, day, hours, minutes, seconds);
    return serverTime > targetDate;
}

/**
 * Prüft ob die aktuelle Serverzeit vor dem angegebenen Zeitpunkt liegt
 * @param {number} year - Jahr (4-stellig, z.B. 2024)
 * @param {number} month - Monat (1-12)
 * @param {number} day - Tag des Monats (1-31)
 * @param {number} [hours=0] - Stunden (0-23)
 * @param {number} [minutes=0] - Minuten (0-59)
 * @param {number} [seconds=0] - Sekunden (0-59)
 * @returns {boolean} - True, wenn die Serverzeit vor dem angegebenen Zeitpunkt liegt
 * @throws {Error} - Wenn die Serverzeit nicht initialisiert wurde oder ungültige Datums-/Zeitwerte übergeben werden
 */
export function isBefore(year, month, day, hours = 0, minutes = 0, seconds = 0) {
    checkServerTime();
    validateDateTime(year, month, day, hours, minutes, seconds);

    const targetDate = new Date(year, month - 1, day, hours, minutes, seconds);
    return serverTime < targetDate;
}

/**
 * Vergleicht ob die aktuelle Uhrzeit zwischen zwei Uhrzeiten liegt
 * @param {number} startHours - Startstunde (0-23)
 * @param {number} startMinutes - Startminuten (0-59)
 * @param {number} endHours - Endstunde (0-23)
 * @param {number} endMinutes - Endminuten (0-59)
 * @returns {boolean} - True wenn die aktuelle Zeit zwischen Start- und Endzeit liegt
 * @throws {Error} - Wenn ungültige Zeitwerte übergeben werden
 */
export function isTimeBetween(startHours, startMinutes, endHours, endMinutes) {
    checkServerTime();
    validateDateTime(2025, 1, 1, startHours, startMinutes);
    validateDateTime(2025, 1, 1, endHours, endMinutes);

    // Prüfe ob alle Parameter Zahlen sind
    if (!Number.isInteger(startHours) || !Number.isInteger(startMinutes) ||
        !Number.isInteger(endHours) || !Number.isInteger(endMinutes)) {
        throw new Error('Alle Zeitparameter müssen ganze Zahlen sein');
    }

    // Prüfe Stunden (0-23)
    if (startHours < 0 || startHours > 23 || endHours < 0 || endHours > 23) {
        throw new Error('Stunden müssen zwischen 0 und 23 liegen');
    }

    // Prüfe Minuten (0-59)
    if (startMinutes < 0 || startMinutes > 59 || endMinutes < 0 || endMinutes > 59) {
        throw new Error('Minuten müssen zwischen 0 und 59 liegen');
    }

    const current = serverTime.getHours() * 60 + serverTime.getMinutes();
    const start = startHours * 60 + startMinutes;
    const end = endHours * 60 + endMinutes;

    // Handle wenn die Endzeit am nächsten Tag liegt (z.B. 22:00 bis 06:00)
    if (end < start) {
        return current >= start || current <= end;
    }

    return current >= start && current <= end;
}